import React, { useMemo, useRef, useState, MouseEvent } from 'react';
import { Stack, Typography } from '@mui/material';
import cx from 'classnames';
import { useHistory } from 'react-router-dom';
import {
  FLOW_TYPE_STREAMING,
  FLOW_TYPE_CDC,
  FLOW_TYPE_RAG,
  FLOW_TYPE_ELT,
  FLOW_TYPE_API_SERVER,
} from 'js/constants/pipelineConstants';
import { ReactComponent as StreamingTypeIcon } from 'new/components/assets/flowIconStreaming.svg';
import { ReactComponent as CDCTypeIcon } from 'new/components/assets/flowTypeCDC.svg';
import { ReactComponent as RAGIcon } from 'new/components/assets/flowTypeRAG.svg';
import { ReactComponent as ELTTypeIcon } from 'new/components/assets/flowTypeELT.svg';
import { ReactComponent as APIServerIcon } from 'new/components/icons/fi-rr-browser.svg';
import { ReactComponent as InfoIcon } from 'new/components/icons/fi-rr-info.svg';
import { navigateToFlowCreation } from 'new/utils/flowNavigation';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { useSelector } from 'react-redux';
import { getCurrentProjectId } from 'js/reducers/app/selectors';
import { useIsFlowTypeEnabled } from 'new/hooks/useFlowTypeVisibility';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { CREATE_API_SERVER_FLOW_REQUEST } from 'js/constants/actionTypes';
import flowTypes from 'new/components/flows/types/flowTypes';
import { useRestrictedTiers } from 'new/hooks/useRestrictedTiers';
import { QuickCreateWidgetInfoModal } from './QuickCreateWidgetInfoModal/QuickCreateWidgetInfoModal';

import styles from './QuickCreateWidget.module.scss';

const FLOW_SELECTION_CARDS = {
  [FLOW_TYPE_STREAMING]: {
    title: 'Flex Flow',
    description: 'Flexible data transformation flows',
    icon: <StreamingTypeIcon />,
    iconColor: 'blue',
    flowType: flowTypes.streaming,
  },
  [FLOW_TYPE_ELT]: {
    title: 'ELT',
    description: 'Extract, Load, Transform pipelines',
    icon: <ELTTypeIcon />,
    iconColor: 'green',
    flowType: flowTypes.elt,
  },
  [FLOW_TYPE_CDC]: {
    title: 'CDC',
    description: 'Change Data Capture streams',
    icon: <CDCTypeIcon />,
    iconColor: 'yellow',
    flowType: flowTypes.cdc,
  },
  [FLOW_TYPE_API_SERVER]: {
    title: 'API Server',
    description: 'Create API endpoints for your data',
    icon: <APIServerIcon />,
    iconColor: 'orange',
    flowType: flowTypes.api_server,
  },
  [FLOW_TYPE_RAG]: {
    title: 'RAG',
    description: 'Retrieval Augmented Generation',
    icon: <RAGIcon />,
    iconColor: 'red',
    flowType: flowTypes.genai_rag,
  },
};

export const QuickCreateWidget = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { hasExpressTierLimitedAccess } = useRestrictedTiers();
  const currentProjectId = useSelector(getCurrentProjectId);
  const [openInfoModal, setOpenInfoModal] = useState(false);
  const [selectedFlowType, setSelectedFlowType] = useState<
    (typeof flowTypes)[keyof typeof flowTypes]
  >(flowTypes.streaming);
  const isNavigating = useRef(false);
  const isCreatingApiServerFlow = useIsLoading(CREATE_API_SERVER_FLOW_REQUEST);
  // Check visibility for conditional flow types
  const isELTEnabled = useIsFlowTypeEnabled(flowTypes.elt);
  const isApiServerEnabled = useIsFlowTypeEnabled(flowTypes.api_server);

  // Filter cards to only show available flow types
  const availableCards = useMemo(() => {
    return Object.values(FLOW_SELECTION_CARDS).filter((card) => {
      // Always show streaming, cdc, and genai_rag (always available)
      if (
        card.flowType === flowTypes.streaming ||
        card.flowType === flowTypes.cdc ||
        card.flowType === flowTypes.genai_rag
      ) {
        return true;
      }
      // Show ELT only if enabled
      if (card.flowType === flowTypes.elt) {
        return isELTEnabled;
      }
      // Show API Server only if enabled
      if (card.flowType === flowTypes.api_server) {
        return isApiServerEnabled;
      }
      return false;
    });
  }, [isELTEnabled, isApiServerEnabled]);

  const handleFlowClick = async (flowType: string) => {
    if (isNavigating.current) return;
    isNavigating.current = true;
    try {
      await navigateToFlowCreation({
        history,
        dispatch,
        selectedFlowType: flowType,
        currentProjectId,
        // returnPathOnDismiss: history.location.pathname
      });
    } finally {
      isNavigating.current = false;
    }
  };

  const handleInfoClick = (
    event: MouseEvent,
    flowType: (typeof flowTypes)[keyof typeof flowTypes],
  ) => {
    event.stopPropagation();
    setSelectedFlowType(flowType);
    setOpenInfoModal(true);
  };

  /**
   * If the user is on an express tier, don't show the quick create widget
   */
  if (hasExpressTierLimitedAccess) return null;

  return (
    <div className={cx(styles.quickCreateContainer, isCreatingApiServerFlow && styles.loading)}>
      <Stack direction="column">
        <Typography variant="bodyLarge" className={styles.quickCreateTitle}>
          Quick Create
        </Typography>
        <Typography variant="body2" className={styles.quickCreateSubtext}>
          Spin up a new pipeline in seconds
        </Typography>
      </Stack>
      <div className={styles.flowSelectionCardsContainer}>
        {availableCards.map((card) => (
          <div
            key={card.title}
            className={styles.flowSelectionCard}
            onClick={() => handleFlowClick(card.flowType)}
          >
            <div
              className={styles.flowSelectionCardInfoIcon}
              onClick={(e) => handleInfoClick(e, card.flowType)}
            >
              <InfoIcon />
            </div>
            <div className={cx(styles.flowSelectionCardIcon, styles[`card--${card.iconColor}`])}>
              {card.icon}
            </div>
            <div className={styles.flowSelectionCardText}>
              <Typography variant="bodySmall" className={styles.flowSelectionCardTitle}>
                {card.title}
              </Typography>
              <Typography variant="bodySmall" className={styles.flowSelectionCardDescription}>
                {card.description}
              </Typography>
            </div>
          </div>
        ))}
      </div>
      <QuickCreateWidgetInfoModal
        flowType={selectedFlowType}
        open={openInfoModal}
        onClose={() => {
          setOpenInfoModal(false);
          setSelectedFlowType(flowTypes.streaming);
        }}
      />
    </div>
  );
};
